import React from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
  Chip,
  Divider,
} from "@mui/material";
import { FormattedMessage } from "react-intl";

interface ResumeMiniInterviewAnswer {
  id: number;
  resume_id: number;
  question: string;
  answer: string;
  status: boolean;
  read_by_admin: boolean;
  created_at: string;
  updated_at: string;
}

interface ExpandingMiniInterviewAnswerDetailProps {
  data: ResumeMiniInterviewAnswer;
}

const ExpandingMiniInterviewAnswerDetail: React.FC<
  ExpandingMiniInterviewAnswerDetailProps
> = ({ data }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Card>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" gutterBottom>
                <FormattedMessage id="Mini Interview Answer Details" />
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    gutterBottom
                  >
                    <FormattedMessage id="ID" />
                  </Typography>
                  <Typography variant="body1">{data.id}</Typography>
                </Box>

                <Box>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    gutterBottom
                  >
                    <FormattedMessage id="Resume ID" />
                  </Typography>
                  <Typography variant="body1">{data.resume_id}</Typography>
                </Box>

                <Box>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    gutterBottom
                  >
                    <FormattedMessage id="Status" />
                  </Typography>
                  <Chip
                    label={data.status ? "Active" : "Inactive"}
                    color={data.status ? "success" : "error"}
                    size="small"
                  />
                </Box>

                <Box>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    gutterBottom
                  >
                    <FormattedMessage id="Read by Admin" />
                  </Typography>
                  <Chip
                    label={data.read_by_admin ? "Read" : "Unread"}
                    color={data.read_by_admin ? "success" : "warning"}
                    size="small"
                  />
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    gutterBottom
                  >
                    <FormattedMessage id="Created At" />
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(data.created_at)}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    gutterBottom
                  >
                    <FormattedMessage id="Updated At" />
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(data.updated_at)}
                  </Typography>
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <Box>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  <FormattedMessage id="Question" />
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    backgroundColor: "grey.50",
                    p: 2,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "grey.200",
                  }}
                >
                  {data.question}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  <FormattedMessage id="Answer" />
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    backgroundColor: "grey.50",
                    p: 2,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "grey.200",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {data.answer}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ExpandingMiniInterviewAnswerDetail;
